// client/src/coms/views/canvas_parts/CanvasDesktopLayout.js
import React from 'react';
import CanvasTopMenuBar from './CanvasTopMenuBar';
import CanvasLeftToolRail from './CanvasLeftToolRail';
import CanvasRightDock from './CanvasRightDock';
import CanvasStatusBar from './CanvasStatusBar';

const CANVAS_TABS_VISIBLE_KEY = 'voxemera_canvas_tabs_visible';

export default function CanvasDesktopLayout({
  name,
  activeToolLabel,
  activeBrushLabel,
  strokeColor,
  setStrokeColor,
  activeToolOpacity,
  onToolOpacityChange,
  brushSize,
  setBrushSize,
  brushSoftness,
  setBrushSoftness,
  jitterAmount,
  setJitterAmount,
  pencilOrientation,
  setPencilOrientation,
  pencilGrade,
  setPencilGrade,
  calligraphyAutoEnabled,
  setCalligraphyAutoEnabled,
  calligraphySettings,
  setCalligraphySettings,
  airbrushDistance,
  setAirbrushDistance,
  airbrushPaintFlow,
  setAirbrushPaintFlow,
  airbrushPressurePaintFlowEnabled,
  setAirbrushPressurePaintFlowEnabled,
  airbrushAirPressure,
  setAirbrushAirPressure,
  airbrushNeedleLimit,
  setAirbrushNeedleLimit,
  liquifySettings,
  setLiquifySettings,
  displayZoomPercent,
  viewRotation,
  documentSize,
  isOwner,
  savingDocumentSize,
  onSetDocumentSize,
  historyCount,
  redoCount,
  onBack,
  onUndo,
  onRedo,
  onSaveCanvas,
  onSaveCanvasProject,
  onLoadCanvasProject,
  onImportCanvasImage,
  onExportModel,
  modelViewOpen,
  onToggleModelView,
  onSaveCanvasHistory,
  onClearCanvas,
  onZoomOut,
  onZoomReset,
  onZoomIn,
  activeTool,
  activeBrushPreset,
  setActiveTool,
  setActiveBrushPreset,
  importedBrushPresets,
  activeImportedBrushId,
  setActiveImportedBrushId,
  onAddImportedBrushPresets,
  onOpenFilters,
  replayPanelOpen,
  onToggleReplayPanel,
  layersPanel,
  sidebarExtensionPanel,
  activeLayer,
  selectionState,
  onSliderPreviewChange,
  onOpenCanvasAreas,
  canOpenCanvasAreas,
  onOpenCanvasGame,
  onOpenPartyGames,
  canOpenCanvasGame,
  onEnableAnimation,
  onDisableAnimation,
  canEnableAnimation,
  animationEnabled,
  animationPanel,
  canvasId,
  canvasTabs = [],
  canvasTabBusy = false,
  onOpenCanvasTab,
  onCreateCanvasTab,
  onRenameCanvasTab,
  onCopyCanvasTab,
  onDeleteCanvasTab,
  onReorderCanvasTabs,
  currentUserId,
  modelToolbarControls,
  gamePanel,
  // Set when a drawing game embeds the editor: hides project, document and Canvas-only features and
  // passes the game's tool, import, undo and clear rules to the rail and the top bar.
  gameMode = null,
  children,
}) {
  const [mobileView, setMobileView] = React.useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
  ));
  const [mobileRightDockOpen, setMobileRightDockOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const swipeRef = React.useRef(null);
  const [tabContextMenu, setTabContextMenu] = React.useState(null);
  const [draggedTabId, setDraggedTabId] = React.useState(0);
  const [tabActionBusy, setTabActionBusy] = React.useState(false);
  const [canvasTabsVisible, setCanvasTabsVisible] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(CANVAS_TABS_VISIBLE_KEY) !== '0';
    } catch {
      return true;
    }
  });

  const toggleCanvasTabsVisible = React.useCallback(() => {
    setCanvasTabsVisible((visible) => {
      const nextVisible = !visible;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(CANVAS_TABS_VISIBLE_KEY, nextVisible ? '1' : '0');
        } catch {}
      }
      return nextVisible;
    });
    setTabContextMenu(null);
  }, []);

  // File > Paper & Surface > Surface Settings… shows the dock's Paper tab; on
  // phones the dock is a drawer, so open it.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !mobileView) return undefined;
    const openDock = () => setMobileRightDockOpen(true);
    window.addEventListener('vx-open-paper-settings', openDock);
    return () => window.removeEventListener('vx-open-paper-settings', openDock);
  }, [mobileView]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const handleChange = (event) => {
      setMobileView(event.matches);
      if (!event.matches) setMobileRightDockOpen(false);
    };

    setMobileView(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  React.useEffect(() => {
    if (!mobileView) return undefined;

    const root = rootRef.current;
    if (!root) return undefined;

    function isInteractiveElement(target) {
      try {
        return !!(target && target.closest && target.closest(
          'input, textarea, select, option, [contenteditable=\"true\"], [data-canvas-no-swipe=\"true\"]'
        ));
      } catch {
        return false;
      }
    }

    function getTouch(event) {
      return event && event.touches && event.touches.length === 1 ? event.touches[0] : null;
    }

    function handleTouchStart(event) {
      if (isInteractiveElement(event.target)) return;

      const touch = getTouch(event);
      if (!touch) return;

      swipeRef.current = {
        active: false,
        startX: Number(touch.clientX || 0),
        startY: Number(touch.clientY || 0),
        lastX: Number(touch.clientX || 0),
        lastY: Number(touch.clientY || 0),
        dockOpen: mobileRightDockOpen,
        startedAt: Date.now(),
      };
    }

    function handleTouchMove(event) {
      const swipe = swipeRef.current;
      if (!swipe) return;

      const touch = getTouch(event);
      if (!touch) return;

      const x = Number(touch.clientX || 0);
      const y = Number(touch.clientY || 0);
      const dx = x - swipe.startX;
      const dy = y - swipe.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      swipe.lastX = x;
      swipe.lastY = y;

      if (!swipe.active) {
        if (ady > 14 && ady > adx * 1.1) {
          swipeRef.current = null;
          return;
        }

        if (adx < 14 || adx < ady * 1.2) return;

        const validDirection = swipe.dockOpen ? dx > 0 : dx < 0;
        if (!validDirection) {
          swipeRef.current = null;
          return;
        }

        swipe.active = true;
      }

      try {
        event.preventDefault();
      } catch {}
    }

    function handleTouchEnd() {
      const swipe = swipeRef.current;
      swipeRef.current = null;
      if (!swipe || !swipe.active) return;

      const dx = swipe.lastX - swipe.startX;
      const dy = swipe.lastY - swipe.startY;
      const travel = Math.abs(dx);
      const elapsed = Math.max(1, Date.now() - swipe.startedAt);
      const velocity = travel / elapsed;
      const horizontal = travel > Math.abs(dy) * 1.15;
      const distanceMet = travel > Math.max(46, window.innerWidth * 0.06);
      const velocityMet = travel > 32 && velocity > 0.45;

      if (!horizontal || (!distanceMet && !velocityMet)) return;

      if (!swipe.dockOpen && dx < 0) {
        setMobileRightDockOpen(true);
      } else if (swipe.dockOpen && dx > 0) {
        setMobileRightDockOpen(false);
      }
    }

    function handleTouchCancel() {
      swipeRef.current = null;
    }

    root.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    root.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    root.addEventListener('touchend', handleTouchEnd, { capture: true, passive: true });
    root.addEventListener('touchcancel', handleTouchCancel, { capture: true, passive: true });

    return () => {
      root.removeEventListener('touchstart', handleTouchStart, true);
      root.removeEventListener('touchmove', handleTouchMove, true);
      root.removeEventListener('touchend', handleTouchEnd, true);
      root.removeEventListener('touchcancel', handleTouchCancel, true);
    };
  }, [mobileRightDockOpen, mobileView]);

  React.useEffect(() => {
    if (!tabContextMenu || typeof window === 'undefined') return undefined;
    const closeMenu = () => setTabContextMenu(null);
    window.addEventListener('pointerdown', closeMenu);
    window.addEventListener('blur', closeMenu);
    return () => {
      window.removeEventListener('pointerdown', closeMenu);
      window.removeEventListener('blur', closeMenu);
    };
  }, [tabContextMenu]);

  const safeCanvasTabs = Array.isArray(canvasTabs) ? canvasTabs : [];

  function moveCanvasTab(draggedIdValue, targetIdValue) {
    const draggedId = Number(draggedIdValue);
    const targetId = Number(targetIdValue);
    if (!draggedId || !targetId || draggedId === targetId) return;
    const fromIndex = safeCanvasTabs.findIndex((tab) => Number(tab && tab.id) === draggedId);
    const targetIndex = safeCanvasTabs.findIndex((tab) => Number(tab && tab.id) === targetId);
    if (fromIndex < 0 || targetIndex < 0) return;
    const reordered = [...safeCanvasTabs];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    if (typeof onReorderCanvasTabs === 'function') onReorderCanvasTabs(reordered);
  }

  async function renameCanvasTab(canvasTab) {
    if (typeof onRenameCanvasTab !== 'function' || tabActionBusy) return;
    const currentName = String((canvasTab && canvasTab.name) || '').trim() || 'Untitled Canvas';
    const nextName = window.prompt('Rename canvas', currentName);
    if (nextName == null || !String(nextName).trim() || String(nextName).trim() === currentName) return;
    setTabActionBusy(true);
    try {
      await onRenameCanvasTab(canvasTab, String(nextName).trim());
    } finally {
      setTabActionBusy(false);
      setTabContextMenu(null);
    }
  }

  async function copyCanvasTab(canvasTab) {
    if (typeof onCopyCanvasTab !== 'function' || tabActionBusy) return;
    setTabActionBusy(true);
    try {
      await onCopyCanvasTab(canvasTab);
    } finally {
      setTabActionBusy(false);
      setTabContextMenu(null);
    }
  }

  async function deleteCanvasTab(canvasTab) {
    if (typeof onDeleteCanvasTab !== 'function' || tabActionBusy) return;
    const tabName = String((canvasTab && canvasTab.name) || '').trim() || 'Untitled Canvas';
    if (!window.confirm(`Delete “${tabName}”? This cannot be undone.`)) return;
    setTabActionBusy(true);
    try {
      await onDeleteCanvasTab(canvasTab, safeCanvasTabs);
    } finally {
      setTabActionBusy(false);
      setTabContextMenu(null);
    }
  }

  // On phones the right dock is hidden behind a swipe, so the game floats over the canvas instead.
  const enrichedSidebarExtensionPanel = React.isValidElement(sidebarExtensionPanel)
    ? React.cloneElement(sidebarExtensionPanel, {
      gamePanel: mobileView ? null : gamePanel,
      setActiveTool,
      setActiveBrushPreset,
      importedBrushPresets,
      activeImportedBrushId,
      setActiveImportedBrushId,
      onAddImportedBrushPresets,
    })
    : sidebarExtensionPanel;

  return (
    <div
      ref={rootRef}
      className="coms-view canvas-desktop-room-view"
      style={{
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#2f2f2f',
        position: 'relative',
        touchAction: mobileView ? 'pan-y' : 'auto',
      }}
    >
      <CanvasTopMenuBar
        gameMode={gameMode}
        name={name}
        activeToolLabel={activeToolLabel}
        activeBrushLabel={activeBrushLabel}
        strokeColor={strokeColor}
        setStrokeColor={setStrokeColor}
        activeTool={activeTool}
        activeBrushPreset={activeBrushPreset}
        importedBrushPresets={importedBrushPresets}
        activeImportedBrushId={activeImportedBrushId}
        setActiveImportedBrushId={setActiveImportedBrushId}
        onAddImportedBrushPresets={onAddImportedBrushPresets}
        activeToolOpacity={activeToolOpacity}
        onToolOpacityChange={onToolOpacityChange}
        brushSize={brushSize}
        setBrushSize={setBrushSize}
        brushSoftness={brushSoftness}
        setBrushSoftness={setBrushSoftness}
        jitterAmount={jitterAmount}
        setJitterAmount={setJitterAmount}
        pencilOrientation={pencilOrientation}
        setPencilOrientation={setPencilOrientation}
        pencilGrade={pencilGrade}
        setPencilGrade={setPencilGrade}
        calligraphyAutoEnabled={calligraphyAutoEnabled}
        setCalligraphyAutoEnabled={setCalligraphyAutoEnabled}
        calligraphySettings={calligraphySettings}
        setCalligraphySettings={setCalligraphySettings}
        airbrushDistance={airbrushDistance}
        setAirbrushDistance={setAirbrushDistance}
        airbrushPaintFlow={airbrushPaintFlow}
        setAirbrushPaintFlow={setAirbrushPaintFlow}
        airbrushPressurePaintFlowEnabled={airbrushPressurePaintFlowEnabled}
        setAirbrushPressurePaintFlowEnabled={setAirbrushPressurePaintFlowEnabled}
        airbrushAirPressure={airbrushAirPressure}
        setAirbrushAirPressure={setAirbrushAirPressure}
        airbrushNeedleLimit={airbrushNeedleLimit}
        setAirbrushNeedleLimit={setAirbrushNeedleLimit}
        liquifySettings={liquifySettings}
        setLiquifySettings={setLiquifySettings}
        displayZoomPercent={displayZoomPercent}
        viewRotation={viewRotation}
        documentSize={documentSize}
        isOwner={isOwner}
        savingDocumentSize={savingDocumentSize}
        onSetDocumentSize={onSetDocumentSize}
        historyCount={historyCount}
        redoCount={redoCount}
        onBack={onBack}
        onUndo={onUndo}
        onRedo={onRedo}
        onSaveCanvas={onSaveCanvas}
        onSaveCanvasProject={onSaveCanvasProject}
        onLoadCanvasProject={onLoadCanvasProject}
        onImportCanvasImage={onImportCanvasImage}
        onExportModel={onExportModel}
        modelViewOpen={modelViewOpen}
        onToggleModelView={onToggleModelView}
        onSaveCanvasHistory={onSaveCanvasHistory}
        onClearCanvas={onClearCanvas}
        onZoomOut={onZoomOut}
        onZoomReset={onZoomReset}
        onZoomIn={onZoomIn}
        onSliderPreviewChange={onSliderPreviewChange}
        onOpenCanvasAreas={onOpenCanvasAreas}
        canOpenCanvasAreas={canOpenCanvasAreas}
        onOpenCanvasGame={onOpenCanvasGame}
        onOpenPartyGames={onOpenPartyGames}
        canOpenCanvasGame={canOpenCanvasGame}
        onEnableAnimation={onEnableAnimation}
        onDisableAnimation={onDisableAnimation}
        canEnableAnimation={canEnableAnimation}
        animationEnabled={animationEnabled}
        modelToolbarControls={modelToolbarControls}
      />

      <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <CanvasLeftToolRail
          isToolAllowed={gameMode ? gameMode.isToolAllowed : null}
          activeTool={activeTool}
          activeBrushPreset={activeBrushPreset}
          activeImportedBrushId={activeImportedBrushId}
          setActiveTool={setActiveTool}
          setActiveBrushPreset={setActiveBrushPreset}
          setActiveImportedBrushId={setActiveImportedBrushId}
          onOpenFilters={onOpenFilters}
          replayPanelOpen={replayPanelOpen}
          onToggleReplayPanel={onToggleReplayPanel}
          canvasTabsVisible={canvasTabsVisible}
          onToggleCanvasTabs={gameMode ? null : toggleCanvasTabsVisible}
          modelViewOpen={modelViewOpen}
        />


        <main
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: '#858585',
            overflow: 'hidden',
          }}
        >
          {children}
        </main>

        {mobileView && gamePanel ? (
          <div
            data-canvas-no-swipe="true"
            data-testid="sketch-relay-mobile-sheet"
            style={{
              position: 'absolute',
              left: 62,
              right: 8,
              bottom: 8,
              maxHeight: '52%',
              overflowY: 'auto',
              zIndex: 30,
              borderRadius: 10,
              boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
            }}
          >
            {gamePanel}
          </div>
        ) : null}

        {mobileView && mobileRightDockOpen ? (
          <button
            type="button"
            aria-label="Close canvas sidebar"
            onClick={() => setMobileRightDockOpen(false)}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 39,
              border: 0,
              padding: 0,
              margin: 0,
              background: 'rgba(0,0,0,0.26)',
            }}
          />
        ) : null}

        <CanvasRightDock
          strokeColor={strokeColor}
          setStrokeColor={setStrokeColor}
          activeTool={activeTool}
          activeBrushPreset={activeBrushPreset}
          setActiveTool={setActiveTool}
          setActiveBrushPreset={setActiveBrushPreset}
          importedBrushPresets={importedBrushPresets}
          activeImportedBrushId={activeImportedBrushId}
          setActiveImportedBrushId={setActiveImportedBrushId}
          onAddImportedBrushPresets={onAddImportedBrushPresets}
          layersPanel={layersPanel}
          animationPanel={animationPanel}
          animationEnabled={animationEnabled}
          sidebarExtensionPanel={enrichedSidebarExtensionPanel}
          mobileView={mobileView}
          mobileOpen={mobileRightDockOpen}
        />
      </div>

      {canvasTabsVisible && !gameMode ? (
        <div
        data-canvas-no-swipe="true"
        style={{
          flex: '0 0 auto',
          minHeight: 36,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          padding: '4px 8px 0',
          overflowX: 'auto',
          overflowY: 'hidden',
          background: '#242424',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          scrollbarWidth: 'thin',
        }}
      >
        {safeCanvasTabs.map((canvasTab, index) => {
          const tabId = Number(canvasTab && canvasTab.id);
          const active = tabId === Number(canvasId);
          const tabName = String((canvasTab && canvasTab.name) || '').trim() || `Canvas ${index + 1}`;
          return (
            <button
              key={tabId || `canvas-tab-${index}`}
              type="button"
              draggable={!tabActionBusy}
              title={tabName}
              aria-current={active ? 'page' : undefined}
              onClick={() => {
                if (draggedTabId) return;
                if (!active && typeof onOpenCanvasTab === 'function') onOpenCanvasTab(canvasTab);
              }}
              onContextMenu={(event) => {
                if (!isOwner) return;
                event.preventDefault();
                event.stopPropagation();
                setTabContextMenu({ canvasTab, x: event.clientX, y: event.clientY });
              }}
              onDragStart={(event) => {
                setDraggedTabId(tabId);
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(tabId));
              }}
              onDragOver={(event) => {
                if (!draggedTabId || draggedTabId === tabId) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(event) => {
                event.preventDefault();
                const sourceId = Number(event.dataTransfer.getData('text/plain')) || draggedTabId;
                moveCanvasTab(sourceId, tabId);
                setDraggedTabId(0);
              }}
              onDragEnd={() => setDraggedTabId(0)}
              style={{
                flex: '0 0 auto',
                minWidth: 108,
                maxWidth: 220,
                height: 31,
                padding: '0 14px',
                border: active ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(255,255,255,0.08)',
                borderBottom: active ? '2px solid #73a7ff' : '2px solid transparent',
                borderRadius: '7px 7px 0 0',
                background: active ? '#3b3b3b' : '#2c2c2c',
                color: active ? '#ffffff' : 'rgba(255,255,255,0.74)',
                fontSize: 12,
                fontWeight: active ? 700 : 600,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: draggedTabId === tabId ? 'grabbing' : 'grab',
                opacity: draggedTabId === tabId ? 0.55 : 1,
              }}
            >
              {tabName}
            </button>
          );
        })}

        {isOwner ? (
          <button
            type="button"
            aria-label="Create new canvas with the same people"
            title="New canvas with the same people"
            disabled={canvasTabBusy || typeof onCreateCanvasTab !== 'function'}
            onClick={() => {
              if (!canvasTabBusy && typeof onCreateCanvasTab === 'function') onCreateCanvasTab();
            }}
            style={{
              flex: '0 0 auto',
              width: 34,
              height: 31,
              border: '1px solid rgba(255,255,255,0.10)',
              borderBottom: '2px solid transparent',
              borderRadius: '7px 7px 0 0',
              background: canvasTabBusy ? '#292929' : '#333333',
              color: canvasTabBusy ? 'rgba(255,255,255,0.35)' : '#ffffff',
              fontSize: 21,
              lineHeight: 1,
              cursor: canvasTabBusy ? 'wait' : 'pointer',
            }}
          >
            {canvasTabBusy ? '…' : '+'}
          </button>
        ) : null}
      </div>
      ) : null}

      {tabContextMenu ? (
        <div
          role="menu"
          data-canvas-no-swipe="true"
          onPointerDown={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            position: 'fixed',
            left: Math.max(8, Math.min(tabContextMenu.x, (typeof window !== 'undefined' ? window.innerWidth : tabContextMenu.x + 180) - 170)),
            top: Math.max(8, tabContextMenu.y - 8),
            transform: 'translateY(-100%)',
            zIndex: 100000,
            minWidth: 160,
            padding: 6,
            borderRadius: 9,
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(28,28,30,0.98)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.42)',
          }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={tabActionBusy}
            onClick={() => renameCanvasTab(tabContextMenu.canvasTab)}
            style={{ width: '100%', minHeight: 34, border: 0, borderRadius: 6, padding: '7px 10px', background: 'transparent', color: '#fff', textAlign: 'left', fontSize: 13, fontWeight: 700, cursor: tabActionBusy ? 'wait' : 'pointer' }}
          >
            Rename
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={tabActionBusy || typeof onCopyCanvasTab !== 'function'}
            onClick={() => copyCanvasTab(tabContextMenu.canvasTab)}
            style={{ width: '100%', minHeight: 34, border: 0, borderRadius: 6, padding: '7px 10px', background: 'transparent', color: '#fff', textAlign: 'left', fontSize: 13, fontWeight: 700, cursor: tabActionBusy ? 'wait' : 'pointer' }}
          >
            Copy canvas
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={tabActionBusy}
            onClick={() => deleteCanvasTab(tabContextMenu.canvasTab)}
            style={{ width: '100%', minHeight: 34, border: 0, borderRadius: 6, padding: '7px 10px', background: 'transparent', color: '#ff8a8a', textAlign: 'left', fontSize: 13, fontWeight: 700, cursor: tabActionBusy ? 'wait' : 'pointer' }}
          >
            Delete
          </button>
        </div>
      ) : null}

      <CanvasStatusBar
        displayZoomPercent={displayZoomPercent}
        viewRotation={viewRotation}
        documentSize={documentSize}
        activeLayer={activeLayer}
        activeToolLabel={activeToolLabel}
        selectionState={selectionState}
      />
    </div>
  );
}
